import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { InfoComponent } from "@components/info/info.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { IMessageLabel, IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValbaraHandelssortiment } from "../../models/IValbaraHandelssortiment";
import { IMottagningsplats, IProduktdefinitionBasdata, ITradslagsgrupp, ITreeSelectionItem } from "../../models/ProduktdefinitionModels";
import { ProduktdefinitionViewModel } from "../../models/ProduktdefinitionViewModel";
import { ProduktinstruktionService } from "../../services/produktinstruktion.service";
import { ProduktinstruktionUtils } from "../../services/produktinstruktionUtils";

@Component({
    selector: "rask-produktinstruktioner-details-header",
    templateUrl: "./produktinstruktioner-details-header.component.html",
    styleUrls: ["./produktinstruktioner-details-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        SelectorComponent,
        InputNumberComponent,
        TreeSelectorComponent,
        TextareaComponent,
        CheckboxComponent,
        InfoComponent,
        MessagePanelComponent
    ]
})
export class ProduktinstruktionerDetailsHeaderComponent implements OnInit, OnChanges {
    @Input() basData: IProduktdefinitionBasdata;
    form: UntypedFormGroup;
    handelssortimentList: IValbaraHandelssortiment[];
    messageLabels: IMessageLabel[] = [
        { severity: MessageSeverity.Warning,
            labelText: "Varning" }
    ];
    mottagningsplatsItems: IMottagningsplats[];
    mottagningsplatsItemsViol3: IMottagningsplats[];
    produktgrupper: string[];
    regionerItems: ITreeSelectionItem[];
    @Input() rootForm: UntypedFormGroup;
    tradslagItems: ITradslagsgrupp[];
    @Input() validationContext: IValidationContext;
    validationMessages: IMessageModel[] = [];
    @Input() viewModel: ProduktdefinitionViewModel;

    private readonly formBinder = inject(FormBinderService);
    private readonly formId = "produktinstruktioner-details-header";
    private readonly produktinstruktionService = inject(ProduktinstruktionService);

    handelssortimentFormatter = (item: IValbaraHandelssortiment): string => item ? item.violId : undefined;

    handelssortimentViewFormatter = (item: IValbaraHandelssortiment): string => item ? `${item.namn} (${item.violId})` : "";

    mottagningsplatsFormatter = (item: IMottagningsplats): string => item ? item.kod : undefined;

    mottagningsplatsViewFormatter = (item: IMottagningsplats): string => item ? `${item.kod} ${item.namn}` : "";

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.viewModel && changes.viewModel.currentValue !== changes.viewModel.previousValue) {
            this.formBinder.reBindForm(this.form, this.viewModel.headerData);
            this.updateValidationMessages();
        }
    }

    ngOnInit(): void {
        this.tradslagItems = this.basData.tradslagsgrupper;
        this.mottagningsplatsItems = this.basData.mottagningsplatser;
        this.regionerItems = ProduktinstruktionUtils.getRegioner(this.basData.regioner);
        this.mottagningsplatsItemsViol3 = this.getViol3FormatedItems(this.basData.mottagningsplatser);
        this.produktgrupper = this.basData.produktgrupper;
        this.handelssortimentList = this.basData.handelssortiment;
        this.form = this.formBinder.CreateGroup(this.viewModel.headerData);
        this.rootForm.addControl(this.formId, this.form);
        this.updateValidationMessages();
    }

    regionerSortExpression = (item: ITreeSelectionItem): number => item.sekvensnummer;

    tradslagViewFormatter = (item: ITradslagsgrupp): string => item ? `${item.namn}` : "";

    tradslagViewSelectedFormatter = (item: ITradslagsgrupp): string => item ? item.namn : "";

    protected controlChanged($event): void {
        this.validationContext.validate(true);
    }

    protected updateValidationMessages() {
        this.validationMessages = [];
        if (this.viewModel.isUtkast && !this.viewModel.isNew) {
            this.produktinstruktionService.validateProduktinstruktion(this.viewModel.headerData.puid).subscribe(validationMessages => {
                this.validationMessages = this.validationMessages.concat(validationMessages.map(y => ({
                    anchor: undefined,
                    metadata: undefined,
                    message: y.message,
                    severity: this.produktinstruktionService.mapSeverity(y),
                    code: undefined,
                    validationScope: undefined
                })));
            });
        }
        const hsInfo = this.handelssortimentList?.find(x => x.violId === this.viewModel.headerData.handelssortiment);
        if (ObjectUtils.hasValue(hsInfo) && !hsInfo.forProduktion) {
            this.validationMessages.push(
                {
                    anchor: undefined,
                    metadata: undefined,
                    message: `Valt handelssortiment ${ hsInfo.namn } är inte konfigurerat för produktion`,
                    severity: MessageSeverity.Error,
                    code: undefined,
                    validationScope: undefined
                });
        }
    }

    private getViol3FormatedItems(mottagningsplatser: IMottagningsplats[]): IMottagningsplats[] {
        return mottagningsplatser.map(x => ({
            namn: x.namn,
            kod: x.kod.slice(0, -1)
        }));
    }
}
